import { ApiLandingPageVideo } from '@storefronts/common-types'
import { RefObject, useCallback, useMemo, useRef } from 'react'
import { MP4VideoManager } from './video-manager/mP4VideoManager'
import { NativeHLSVideoManager } from './video-manager/nativeHLSVideoManager'
import { VideoSourceInfo } from './video-manager/iVideoManager'

export type { VideoSourceInfo }

type HLSVideos = ApiLandingPageVideo[][]

export const getVideoManager = () => {
    if (!globalThis.document) return new MP4VideoManager()
    // Prefer hls.js over native HLS: Chrome 142+ answers canPlayType('application/
    // vnd.apple.mpegURL') with "maybe" but its built-in HLS player fails on our
    // streams (MEDIA_ERR_SRC_NOT_SUPPORTED). Native is only for MSE-less browsers
    // (iOS Safari), which genuinely play HLS.
    if ('MediaSource' in window) return new MP4VideoManager()
    const videoElement = document.createElement('video')
    const canPlayHls =
        !!videoElement.canPlayType &&
        (videoElement.canPlayType('application/vnd.apple.mpegURL') != '' ||
            videoElement.canPlayType('audio/mpegurl') != '')
    if (canPlayHls) {
        return new NativeHLSVideoManager()
    }

    return new MP4VideoManager()
}

export const useHLSManager = (vids: HLSVideos) => {
    const hlsInstances = useRef<VideoSourceInfo[][]>([])

    const syncedInstances = useMemo(
        () =>
            vids.map((videos) =>
                videos.map((v) => ({
                    manifestUrl: `https://stream.mux.com/${v.playbackId}.m3u8`,
                    hls: null,
                    videoRef: null,
                    playerVideo: v,
                    videoManager: getVideoManager(),
                })),
            ),
        [vids],
    )

    hlsInstances.current = syncedInstances

    const setVideoElementRef = useCallback(
        (
            playlistIndex: number,
            videoIndex: number,
            videoRef: RefObject<HTMLVideoElement>,
        ) => {
            const instance = hlsInstances.current?.[playlistIndex]?.[videoIndex]
            if (!instance) return null
            instance.videoRef = videoRef
        },
        [],
    )

    const resetVideoLoc = useCallback(
        (playlistIndex: number, videoIndex: number) => {
            const instanceObj =
                hlsInstances.current[playlistIndex]?.[videoIndex]
            if (!instanceObj) return
            const videoElement = instanceObj.videoRef?.current
            if (!videoElement) return
            videoElement.currentTime = 0
        },
        [],
    )

    const detachAll = useCallback(() => {
        hlsInstances.current.forEach((playlist) => {
            playlist.forEach((instance) => {
                instance.videoManager.detachInstance(instance)
            })
        })
    }, [])

    return {
        detachAll,
        setVideoElementRef,
        resetVideoLoc,
        hlsInstances,
    }
}
